import {axisStyle} from 'components/charts/styles';
import {cssColorValues} from 'components/system/colors';
import {classNames} from 'utils';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryContainer,
  VictoryGroup,
  VictoryTheme,
} from 'victory';
import styles from './monte_carlo_simulation.module.css';

export const MonteCarloSimulation = ({
  success,
  expectedDistribution,
  outputDistribution,
}: Props) => {
  let bucketNames: Record<string, string>;
  let getX: (bucket: Bucket) => number | string;

  if (isDiscrete(expectedDistribution)) {
    // Discrete random variable.
    bucketNames = Object.fromEntries(
      expectedDistribution.map((bucket, i) => [
        JSON.stringify(bucket.output),
        String.fromCharCode(65 + i), // A, B, C, etc...
      ]),
    );
    getX = (bucket: Bucket) => bucketNames[JSON.stringify(bucket.output)];
  } else {
    // Continuous random variable.
    getX = (bucket: Bucket) => (bucket.output_from + bucket.output_to) / 2;
  }

  const dataExpected = expectedDistribution.map((bucket) => ({x: getX(bucket), y: bucket.weight}));
  const dataOutput = outputDistribution.map((bucket) => ({x: getX(bucket), y: bucket.weight}));
  const outputColor = success ? 'green' : 'red';

  return (
    <div className={styles.container}>
      <VictoryChart
        theme={VictoryTheme.material}
        width={420}
        height={220}
        padding={{left: 10 + (dataExpected[0].x < 0 ? 0 : 40), top: 0, bottom: 30, right: 10}}
        domainPadding={{y: 20}}
        containerComponent={<VictoryContainer responsive={true} />}>
        <VictoryAxis style={axisStyle} />
        <VictoryAxis dependentAxis={true} style={axisStyle} />
        <VictoryGroup>
          <VictoryArea
            data={dataExpected}
            interpolation='basis'
            style={{
              data: {
                fill: cssColorValues.orange,
                fillOpacity: 0.2,
                stroke: cssColorValues.orange,
                strokeWidth: 2,
                strokeOpacity: 1,
              },
            }}
          />
          <VictoryArea
            data={dataOutput}
            interpolation='basis'
            style={{
              data: {
                fill: cssColorValues[outputColor],
                fillOpacity: 0.2,
                stroke: cssColorValues[outputColor],
                strokeWidth: 2,
                strokeOpacity: 0.5,
              },
            }}
          />
        </VictoryGroup>
      </VictoryChart>
      <div className={styles.buckets}>
        <div className={classNames([styles.legend, styles.expected])}>Expected distribution</div>
        <div className={classNames([styles.legend, styles[outputColor]])}>Your distribution</div>
        {bucketNames && (
          <>
            <br />
            Buckets:
            {Object.entries(bucketNames).map(([output, bucketName]) => (
              <div key={bucketName}>
                <strong>{bucketName}</strong>: <code>{output}</code>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

type Props = {
  success: boolean;
  expectedDistribution: Bucket[];
  outputDistribution: Bucket[];
};

type Bucket = {
  output?: any;
  output_from?: number;
  output_to?: number;
  weight: number;
};

export const isMonteCarloSimulation = (
  rawExpectedDistribution: string,
  rawOutputDistribution: string,
) => {
  return isDistribution(rawExpectedDistribution) && isDistribution(rawOutputDistribution);
};

export const isDistribution = (rawDistribution: string) => {
  try {
    const distribution = JSON.parse(rawDistribution);
    if (!Array.isArray(distribution)) return false;
    for (const bucket of distribution) {
      const keys = Object.keys(bucket);
      if (!keys.includes('weight')) return false;
      if (
        !keys.includes('output') &&
        !(keys.includes('output_from') && keys.includes('output_to'))
      ) {
        return false;
      }
    }
  } catch {
    return false;
  }
  return true;
};

const isDiscrete = (distribution: Bucket[]) => {
  // We can determine if it's a discrete distribution by counting the unique outputs of
  // each bucket and checking it matches with the total amount of buckets.
  return [...new Set(distribution.map(({output}) => output))].length === distribution.length;
};
